<script setup>
// Focused player — hosts the voice client (iframe) launched on a specific deck
// via /voice?embed=1&deck=<slug>&autostart=1. The client preselects + auto-starts
// that deck and hides its own welcome/picker chrome (launch-mode), so this view
// shows only the in-quiz player. A same-origin iframe inherits the better-auth
// session cookie. Interim until the player ships as a Web Component.
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@digitalstreams/saas-core/vue/stores/authStore";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const slug = computed(() => route.params.slug);

// Skin by audience — the same split AutoShell already makes for the chrome.
//   signed-in  → "focused":   quiet + neutral, sits inside the app, and has NO art
//                dependency (a tenant's private deck often has no cover, and a
//                photographic player falling back to grey would look broken).
//   anonymous  → "immersive": the deck's OWN cover art becomes the backdrop, so
//                the visitor falls into the card they just clicked.
// mode=auto follows the visitor's device (light/dark). Both are just URL params,
// which is exactly how a host site will pick a theme once embedding lands.
const skin = computed(() => (auth.isLoggedIn ? "focused" : "immersive"));
const src = computed(() =>
  `/voice?embed=1&deck=${encodeURIComponent(slug.value)}&autostart=1&skin=${skin.value}&mode=auto`);

// The frame is sized BY THE PLAYER (it posts irecall:height), not guessed from the
// viewport. The old fixed `innerHeight - 150` pushed the mic and speed controls
// below the iframe's fold on a phone — the learner couldn't see the controls at
// all, and scrolling inside an iframe isn't discoverable. `measured` takes over
// as soon as the first height arrives; the viewport guess is only the first paint.
const measured = ref(0);
const fallback = ref(560);
const iframeHeight = computed(() => `${measured.value || fallback.value}px`);
function recompute() { fallback.value = Math.max(560, window.innerHeight - 150); }

// The embedded player posts this when the user ends a session — return them to
// the native deck picker rather than leaving the (launch-mode-hidden) blank iframe.
function onMessage(e) {
  if (e.origin !== window.location.origin) return;
  if (e.data?.type === "irecall:session-ended") router.push("/topics");
  if (e.data?.type === "irecall:height" && Number.isFinite(e.data.px)) {
    // Clamped: a bad number from the frame must not blow the layout up.
    measured.value = Math.min(Math.max(Math.round(e.data.px), 360), 4000);
  }
}

onMounted(() => { recompute(); window.addEventListener("resize", recompute); window.addEventListener("message", onMessage); });
onBeforeUnmount(() => { window.removeEventListener("resize", recompute); window.removeEventListener("message", onMessage); });
</script>

<template>
  <div class="container-fluid p-0">
    <router-link to="/topics" class="btn btn-sm btn-link px-0 mb-2">
      <i class="bi bi-arrow-left me-1"></i>Choose another topic
    </router-link>
    <iframe
      :src="src"
      title="i-Recall voice quiz"
      allow="microphone; autoplay"
      :style="{ height: iframeHeight }"
      class="rm-play-iframe"
    ></iframe>
  </div>
</template>

<style scoped>
.rm-play-iframe {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  display: block;
}
</style>
